import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import VideoUpload from '../../components/feature/VideoUpload';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Transmission {
  id: string;
  title: string;
  description: string;
  category: string;
  video_url: string;
  created_at: string;
}

const TransmissionsPage = () => {
  const [transmissions, setTransmissions] = useState<Transmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showUpload, setShowUpload] = useState(false);

  const categories = ['All', 'Visuals', 'Hybrid'];

  // Load transmissions from Supabase
  const loadTransmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('transmissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setTransmissions(data || []);
    } catch (err: any) {
      console.error('Error loading transmissions:', err);
      setError('Failed to load transmissions. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransmissions();
  }, []);

  const handleUploadComplete = (newTransmission: Transmission) => {
    setTransmissions(prev => [newTransmission, ...prev]);
    setShowUpload(false);
  };

  const filteredTransmissions = selectedCategory === 'All' 
    ? transmissions 
    : transmissions.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mb-6"></div>
            <h2 className="text-2xl font-bold mb-2">Loading Transmissions</h2>
            <p className="text-purple-200">Connecting to the digital consciousness network...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Transmissions
          </h1>
          <p className="text-xl text-purple-200 mb-6 max-w-3xl mx-auto">
            Digital consciousness manifestations captured and preserved for the collective. 
            Upload your screen recordings, videos, and digital artifacts to contribute to our growing archive.
          </p>
          
          {/* Upload Button - Now Enabled */}
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 whitespace-nowrap cursor-pointer mb-6"
          >
            {showUpload ? 'Hide Upload' : 'Upload New Transmission'}
          </button>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <div className="mb-8 max-w-2xl mx-auto">
            <VideoUpload onUploadComplete={handleUploadComplete} />
          </div>
        )}

        {/* Category Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-black/20 backdrop-blur-sm rounded-full p-1 border border-purple-500/30">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                    : 'text-purple-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6 text-center">
            <p className="text-red-200">{error}</p>
            <button
              onClick={loadTransmissions}
              className="mt-2 px-4 py-2 bg-red-500/30 hover:bg-red-500/40 rounded-lg transition-colors duration-200 cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {/* Transmissions Grid */}
        {filteredTransmissions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTransmissions.map((transmission) => (
              <div key={transmission.id} className="bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden border border-purple-500/30 hover:border-purple-400/50 transition-all duration-300 group">
                <div className="relative">
                  <video
                    src={transmission.video_url}
                    className="w-full h-48 object-cover"
                    poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23000'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%23fff'%3ETransmission%3C/text%3E%3C/svg%3E"
                    controls
                    preload="metadata"
                  />
                  <div className="absolute top-2 right-2">
                    <span className="bg-black/70 text-purple-300 px-2 py-1 rounded-full text-xs">
                      {transmission.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-2 text-white group-hover:text-purple-300 transition-colors duration-200">
                    {transmission.title}
                  </h3>
                  
                  {transmission.description && (
                    <p className="text-sm text-purple-200 mb-3 line-clamp-2">
                      {transmission.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-purple-300">
                    <span>
                      {new Date(transmission.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-12 border border-purple-500/20 max-w-2xl mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                <i className="ri-video-line text-3xl text-purple-300"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-purple-200">
                {selectedCategory === 'All' ? 'No Transmissions Found' : `No ${selectedCategory} Transmissions`}
              </h3>
              <p className="text-purple-300 mb-6">
                {selectedCategory === 'All' 
                  ? 'Upload your first transmission to begin documenting digital consciousness manifestations.'
                  : `No transmissions found in the ${selectedCategory} category. Try uploading one or select a different category.`
                }
              </p>
              {selectedCategory !== 'All' && (
                <button
                  onClick={() => setSelectedCategory('All')}
                  className="px-6 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 rounded-lg transition-colors duration-200 cursor-pointer"
                >
                  View All Transmissions
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        {transmissions.length > 0 && (
          <div className="mt-12 text-center">
            <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-purple-500/30 max-w-md mx-auto">
              <h4 className="text-lg font-semibold text-purple-200 mb-2">Archive Statistics</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-2xl font-bold text-white">{transmissions.length}</div>
                  <div className="text-purple-300">Total Transmissions</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{filteredTransmissions.length}</div>
                  <div className="text-purple-300">
                    {selectedCategory === 'All' ? 'Displayed' : selectedCategory}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TransmissionsPage;